import type { UserClass } from "../api-client/models";
import type { Item } from "../api-client/models/item";
import type { Nation } from "../api-client/models/nation";
import type { RoleSerializerWithPermissions } from "../api-client/models/roleSerializerWithPermissions";
import { PurchaseItem } from "../types/PurchaseItem.ts";
import { PERMISSIONS, PermissionConfig } from "../config/permissions";
import { checkPermission, PermissionResult, canUserSelfApprove } from "../utils/permissionUtils";

let ADMIN_ON = true;

let rolesCache: RoleSerializerWithPermissions[] = [];

export const getCachedRoles = (): RoleSerializerWithPermissions[] => rolesCache;

export const setCachedRoles = (roles: RoleSerializerWithPermissions[]) => {
  rolesCache = roles;
};

export const createPermissionService = (
  getRolesFn: () => RoleSerializerWithPermissions[] = () => rolesCache ?? [],
) => {
  const checkPerm = (
    user: UserClass,
    config: PermissionConfig,
    ...args: any[]
  ): PermissionResult => {
    const roles = getRolesFn();
    return checkPermission(user, roles, config, ADMIN_ON, ...args);
  };

  return {
    setAdminOn: (on: boolean) => {
      ADMIN_ON = on;
    },
    getAdminOn: () => {
      return ADMIN_ON;
    },
    isAdmin: (user: UserClass) => {
      if (!user) {
        return { success: false, message: "Invalid User" };
      }
      if (ADMIN_ON && user.isAdmin) {
        return { success: true, message: "" };
      }
      return { success: false, message: "Must be an admin" };
    },
    canPurchaseItem: (user: UserClass, item: Item) =>
      checkPerm(user, PERMISSIONS.PURCHASE_ITEM, item),
    canUploadFlag: (user: UserClass, nation: Nation) =>
      checkPerm(user, PERMISSIONS.UPLOAD_FLAG, nation.id),
    canEditConstitution: (user: UserClass, nationId: number) =>
      checkPerm(user, PERMISSIONS.EDIT_CONSTITUTION, nationId),
    canEditNationName: (user: UserClass, nationId: number) =>
      checkPerm(user, PERMISSIONS.EDIT_NATION_NAME, nationId),
    canInitiateTrades: (user: UserClass) =>
      checkPerm(user, PERMISSIONS.INITIATE_TRADES),
    canPublishNews: (user: UserClass) =>
      checkPerm(user, PERMISSIONS.PUBLISH_NEWS),
    canEditNews: (user: UserClass, nationId: number) =>
      checkPerm(user, PERMISSIONS.EDIT_NEWS, nationId),
    canComment: (user: UserClass) =>
      checkPerm(user, PERMISSIONS.COMMENT),
    canPurchaseInfrastructure: (user: UserClass) =>
      checkPerm(user, PERMISSIONS.PURCHASE_INFRASTRUCTURE),
    canPurchaseMilitary: (user: UserClass) =>
      checkPerm(user, PERMISSIONS.PURCHASE_MILITARY),
    canPlacePurchase: (user: UserClass, purchase: PurchaseItem) => {
      // Special handling: dynamically choose between military and infrastructure permissions
      const config = purchase.item.isMilitary
        ? PERMISSIONS.PLACE_MILITARY
        : PERMISSIONS.PLACE_INFRASTRUCTURE;
      return checkPerm(user, config);
    },
    canInitiateTradeAlliance: (user: UserClass) =>
      checkPerm(user, PERMISSIONS.INITIATE_TRADE_ALLIANCE),
    canInitiatePeaceTreaty: (user: UserClass) =>
      checkPerm(user, PERMISSIONS.INITIATE_PEACE_TREATY),
    canInitiateMilitaryAlliance: (user: UserClass) =>
      checkPerm(user, PERMISSIONS.INITIATE_MILITARY_ALLIANCE),
    canInitiateWarning: (user: UserClass) =>
      checkPerm(user, PERMISSIONS.INITIATE_WARNING),
    canInitiateWar: (user: UserClass) =>
      checkPerm(user, PERMISSIONS.INITIATE_WAR),
    canInitiateSubpoena: (user: UserClass) =>
      checkPerm(user, PERMISSIONS.INITIATE_SUBPOENA),
    canEditProfile: (user: UserClass, id: number) =>
      checkPerm(user, PERMISSIONS.EDIT_PROFILE, id),
    canEditPurchaseName: (user: UserClass) =>
      checkPerm(user, PERMISSIONS.EDIT_PURCHASE_NAME),
    hasPermission: (user: UserClass, permission: string) => {
      return !!(user.permissions && user.permissions[permission]);
    },
    canSelfApprovePermission: (user: UserClass | undefined, permission: string) => {
      if (!user) {
        return false;
      }
      const roles = getRolesFn();
      return canUserSelfApprove(user, roles, permission);
    },
  };
};

export type PermissionService = ReturnType<typeof createPermissionService>;

export const permissionService = createPermissionService();
